import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Alert,
  IconButton,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useParams } from "react-router-dom";
import ItemService from "../../../apis/ItemService";
import {
  BLOOMS_TAXONOMY_SKILLS,
  normalizeSkillTypeValue,
} from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";

const RankingItemDetail = () => {
  const { itemId } = useParams();
  const isNew = !itemId || itemId === "new";

  const [item, setItem] = useState({
    Type: "Ranking",
    Name: "",
    points: 1,
    skilltype: "",
    itemcat: "",
    Direction: "LTR",
    Title: "",
    Options: [{ id: 1, Value: "", Rationale: "" }],
  });

  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [isRtl, setIsRtl] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);

  // 🧠 Detect Arabic text
  const detectIsArabic = (text) => /[\u0600-\u06FF]/.test(text);

  useEffect(() => {
    const taxonomyLoad = async () => {
      try {
        const taxonomy = await ItemService.fetchTaxonomy("arabic");
        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomy));
      } catch {
        setTaxonomyOptions([]);
      }
    };
    taxonomyLoad();
  }, []);

  // auto set RTL based on first option text
  useEffect(() => {
    const firstValue = item.Options?.[0]?.Value || "";
    if (firstValue) {
      setIsRtl(detectIsArabic(firstValue));
    }
  }, [item.Options]);

  const handleOptionChange = (index, field, value) => {
    setItem((prev) => {
      const opts = [...prev.Options];
      opts[index] = { ...opts[index], [field]: value };
      return { ...prev, Options: opts };
    });
  };

  const addOption = () =>
    setItem((prev) => ({
      ...prev,
      Options: [
        ...prev.Options,
        { id: prev.Options.length + 1, Value: "", Rationale: "" },
      ],
    }));

  const deleteOption = (index) =>
    setItem((prev) => ({
      ...prev,
      Options: prev.Options.filter((_, i) => i !== index),
    }));

  useEffect(() => {
    setPreviewItems(item.Options.map((opt) => opt.Value));
  }, [item.Options]);

  // ✅ Fixed JSON builder with proper Rationale
  const handleSave = useCallback(async () => {
    if (!item.Name || !item.itemcat || !item.skilltype) {
      setSaveError("Please fill all required fields.");
      return;
    }

    const timestamp = new Date().toISOString();

    const payload = {
      PK: `itemcat#${item.itemcat};skilltype#${item.skilltype}`,
      SK: `item#${itemId || "new"}`,
      dateCreated: timestamp,
      dateUpdated: timestamp,
      GPK1: "item",
      GSK1: `itemcat#${item.itemcat};skilltype#${item.skilltype}`,
      Name: item.Name,
      Options: item.Options.map((opt, i) => ({
        id: i + 1,
        Rationale: opt.Rationale || "",
        Value: opt.Value || "",
      })),
      points: item.points,
      Title:
        item.Title ||
        "<div>Arrange the different parts in the correct order</div>",
      Type: "Ranking",
      version: "v1",
    };

    try {
      setSaving(true);
      await ItemService.saveItem(payload, isNew ? undefined : itemId);
      setSaveSuccess(true);
      setSaveError(null);
    } catch (err) {
      setSaveError(err?.message || "Failed to save item.");
    } finally {
      setSaving(false);
    }
  }, [item, isNew, itemId]);

  const jsonPreview = useMemo(
    () =>
      JSON.stringify(
        {
          PK: `itemcat#${item.itemcat};skilltype#${item.skilltype}`,
          SK: `item#${itemId || "new"}`,
          createdBy: "3b4f5146-b43f-4508-b136-3490cfa7dd11",
          dateCreated: new Date().toISOString(),
          dateUpdated: new Date().toISOString(),
          GPK1: "item",
          GSK1: `itemcat#${item.itemcat};skilltype#${item.skilltype}`,
          Name: item.Name,
          Options: item.Options.map((opt, i) => ({
            id: i + 1,
            Rationale: opt.Rationale || "",
            Value: opt.Value || "",
          })),
          points: item.points,
          Title:
            item.Title ||
            "<div>Arrange the different parts in the correct order</div>",
          Type: "Ranking",
          updatedBy: "3b4f5146-b43f-4508-b136-3490cfa7dd11",
          version: "v1",
        },
        null,
        2
      ),
    [item, itemId]
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label="Ranking" color="secondary" />
            <Typography variant="h6" fontWeight={600}>
              {isNew ? "Create Ranking Item" : "Edit Ranking Item"}
            </Typography>
          </Stack>
          <Button variant="outlined" onClick={() => setJsonDialogOpen(true)}>
            View JSON
          </Button>
        </Stack>

        {saveSuccess && (
          <Alert severity="success">Item saved successfully!</Alert>
        )}
        {saveError && <Alert severity="error">{saveError}</Alert>}

        {/* Basic Fields */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Item Name"
              fullWidth
              required
              value={item.Name}
              onChange={(e) => setItem((p) => ({ ...p, Name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Points"
              type="number"
              fullWidth
              value={item.points}
              onChange={(e) =>
                setItem((p) => ({ ...p, points: Number(e.target.value) }))
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth required>
              <InputLabel>Skill Type</InputLabel>
              <Select
                value={item.skilltype}
                label="Skill Type"
                onChange={(e) =>
                  setItem((p) => ({
                    ...p,
                    skilltype: normalizeSkillTypeValue(e.target.value),
                  }))
                }
              >
                {BLOOMS_TAXONOMY_SKILLS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Item Category</InputLabel>
              <Select
                value={item.itemcat}
                label="Item Category"
                onChange={(e) =>
                  setItem((p) => ({ ...p, itemcat: e.target.value }))
                }
              >
                {taxonomyOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Prompt Editor */}
        <Divider />
        <Typography variant="subtitle1" fontWeight={600}>
          Prompt / Question
        </Typography>
        <ReactQuill
          theme="snow"
          value={item.Title}
          onChange={(v) => setItem((p) => ({ ...p, Title: v }))}
          placeholder="Write your question or instructions..."
          style={{ height: "150px", marginBottom: "40px" }}
        />

        {/* Options */}
        <Divider />
        <Typography variant="subtitle1" fontWeight={600}>
          Ranking Items
        </Typography>
        {item.Options.map((opt, i) => (
          <Stack key={i} spacing={1} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label={`Item ${i + 1}`}
              value={opt.Value}
              onChange={(e) => handleOptionChange(i, "Value", e.target.value)}
              inputProps={{
                dir: isRtl ? "rtl" : "ltr",
                style: { textAlign: isRtl ? "right" : "left" },
              }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Rationale"
              value={opt.Rationale}
              onChange={(e) =>
                handleOptionChange(i, "Rationale", e.target.value)
              }
            />
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => deleteOption(i)}
              sx={{ alignSelf: "flex-end", mt: -1 }}
            >
              Remove
            </Button>
          </Stack>
        ))}
        <Button startIcon={<AddIcon />} onClick={addOption}>
          Add Item
        </Button>

        {/* RTL Toggle */}
        <FormControlLabel
          control={
            <Switch checked={isRtl} onChange={() => setIsRtl((p) => !p)} />
          }
          label="Right-to-left direction"
        />

        {/* Preview */}
        <Divider />
        <Typography variant="subtitle1" fontWeight={600}>
          Preview
        </Typography>
        <DragDropContext
          onDragEnd={(result) => {
            if (!result.destination) return;
            const src = result.source.index;
            const dest = result.destination.index;
            setPreviewItems((prev) => {
              const next = [...prev];
              const [moved] = next.splice(src, 1);
              next.splice(dest, 0, moved);
              return next;
            });
          }}
        >
          <Droppable droppableId="ranking-preview">
            {(provided) => (
              <Stack
                ref={provided.innerRef}
                {...provided.droppableProps}
                spacing={1}
                sx={{
                  direction: isRtl ? "rtl" : "ltr",
                  textAlign: isRtl ? "right" : "left",
                }}
              >
                {previewItems.map((val, idx) => (
                  <Draggable
                    key={`${val}-${idx}`}
                    draggableId={`${val}-${idx}`}
                    index={idx}
                  >
                    {(draggableProvided, snapshot) => (
                      <Box
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                        sx={{
                          p: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          backgroundColor: snapshot.isDragging
                            ? "action.hover"
                            : "background.paper",
                          cursor: "grab",
                        }}
                      >
                        {val || <em>(empty)</em>}
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>

        {/* Save */}
        <Stack direction="row" justifyContent="flex-end">
          <Button
            startIcon={<SaveIcon />}
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Item"}
          </Button>
        </Stack>
      </Stack>

      {/* JSON Dialog */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Item JSON Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "monospace",
              fontSize: 13,
            }}
          >
            {jsonPreview}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RankingItemDetail;
